import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { analyzeCVByID } from '../../../services'
import { CVAnalysisResult } from '../../../types'

interface UseCVAnalyzeArgs {
    cvID: number | undefined
    cvUrl: string | undefined
    cachedAnalysis: CVAnalysisResult | null | undefined
    onSuccess?: () => void
}

// useCVAnalyze owns analyze state + handlers for a single CV detail page.
// Extracted out of ResultCVInformation to keep that component below the
// cognitive complexity threshold flagged by the linter.
export const useCVAnalyze = ({
    cvID,
    cvUrl,
    cachedAnalysis,
    onSuccess,
}: UseCVAnalyzeArgs) => {
    const { t: tResume } = useTranslation('resume')
    const [isEvaluating, setIsEvaluating] = useState(false)
    const [analysisResult, setAnalysisResult] =
        useState<CVAnalysisResult | null>(cachedAnalysis ?? null)

    const runAnalyze = async (payload: { job_id?: number; jd?: string }) => {
        if (!cvID || !cvUrl) {
            toast.error(tResume('evaluate-cv-no-file'))
            return
        }
        setIsEvaluating(true)
        try {
            const response = await analyzeCVByID(cvID, payload)
            if (response?.result) {
                setAnalysisResult(response.result)
                toast.success(tResume('evaluate-cv-success'))
                onSuccess?.()
            }
        } catch {
            toast.error(tResume('evaluate-cv-failed'))
        } finally {
            setIsEvaluating(false)
        }
    }

    return {
        isEvaluating,
        analysisResult,
        // No JD — just structured profile
        buildProfile: () => runAnalyze({}),
        // With JD (from job_id or raw text) — includes evaluation block
        evaluateAgainstJD: (payload: { job_id?: number; jd?: string }) =>
            runAnalyze(payload),
    }
}
